/**
 * ActiveObject 双指手势（缩放/旋转/平移）
 * 仅被 editUtil.js 导入并 re-export，工具函数仅在本文件内使用。
 */

import * as constant from './constant.js';

// 全局开关：默认开启
let activeObjectPinchGestureEnabled = true;

/**
 * 设置 ActiveObject 双指手势开关（默认 true）
 * @param {boolean} enabled
 */
export function setActiveObjectPinchGestureEnabled(enabled) {
  activeObjectPinchGestureEnabled = enabled !== false;
}

// 每个 canvas 只绑定一次，便于卸载
const activeObjectPinchGestureMap =
  typeof WeakMap !== 'undefined' ? new WeakMap() : null;

function clampNumber(num, min, max) {
  if (typeof num !== 'number' || isNaN(num)) {
    return min;
  }
  if (typeof min === 'number' && num < min) {
    return min;
  }
  if (typeof max === 'number' && num > max) {
    return max;
  }
  return num;
}

function getTouchDistance(p1, p2) {
  if (!p1 || !p2) {
    return 0;
  }
  const dx = (p2.pageX || 0) - (p1.pageX || 0);
  const dy = (p2.pageY || 0) - (p1.pageY || 0);
  return Math.sqrt(dx * dx + dy * dy);
}

function getTouchAngle(p1, p2) {
  if (!p1 || !p2) {
    return 0;
  }
  const dx = (p2.pageX || 0) - (p1.pageX || 0);
  const dy = (p2.pageY || 0) - (p1.pageY || 0);
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

function getTouchCenter(p1, p2) {
  const p1ClientX =
    p1 && p1.clientX !== undefined ? p1.clientX : p1 && p1.pageX;
  const p1ClientY =
    p1 && p1.clientY !== undefined ? p1.clientY : p1 && p1.pageY;
  const p2ClientX =
    p2 && p2.clientX !== undefined ? p2.clientX : p2 && p2.pageX;
  const p2ClientY =
    p2 && p2.clientY !== undefined ? p2.clientY : p2 && p2.pageY;
  return {
    pageX: ((p1 && p1.pageX) || 0) / 2 + ((p2 && p2.pageX) || 0) / 2,
    pageY: ((p1 && p1.pageY) || 0) / 2 + ((p2 && p2.pageY) || 0) / 2,
    clientX: (p1ClientX || 0) / 2 + (p2ClientX || 0) / 2,
    clientY: (p1ClientY || 0) / 2 + (p2ClientY || 0) / 2
  };
}

function getCanvasPointFromPage(upperCanvasEl, pagePoint) {
  if (!upperCanvasEl || !pagePoint) {
    return { x: 0, y: 0 };
  }
  const rect =
    typeof upperCanvasEl.getBoundingClientRect === 'function'
      ? upperCanvasEl.getBoundingClientRect()
      : null;
  if (!rect) {
    return { x: 0, y: 0 };
  }

  // 将页面坐标映射到 canvas 像素坐标（考虑 css 缩放）
  const cssW = rect.width || 1;
  const cssH = rect.height || 1;
  const canvasW = upperCanvasEl.width || cssW;
  const canvasH = upperCanvasEl.height || cssH;
  // rect 基于 viewport（client 坐标），优先使用 clientX/clientY；否则用 pageX/pageY 扣掉滚动偏移
  const scrollLeft =
    typeof window !== 'undefined'
      ? window.pageXOffset ||
        (document &&
          document.documentElement &&
          document.documentElement.scrollLeft) ||
        0
      : 0;
  const scrollTop =
    typeof window !== 'undefined'
      ? window.pageYOffset ||
        (document &&
          document.documentElement &&
          document.documentElement.scrollTop) ||
        0
      : 0;
  const clientX =
    pagePoint.clientX !== undefined
      ? pagePoint.clientX
      : (pagePoint.pageX || 0) - scrollLeft;
  const clientY =
    pagePoint.clientY !== undefined
      ? pagePoint.clientY
      : (pagePoint.pageY || 0) - scrollTop;

  const x = (clientX - rect.left) * (canvasW / cssW);
  const y = (clientY - rect.top) * (canvasH / cssH);
  return { x: x, y: y };
}

function transformDeltaByViewport(fabricCanvas, dx, dy) {
  // 如果存在 viewportTransform（例如缩放/平移视口），把屏幕像素增量转换到对象坐标系增量
  const vt = fabricCanvas && fabricCanvas.viewportTransform;
  if (vt && typeof vt.length === 'number' && vt.length >= 6) {
    const scaleX = typeof vt[0] === 'number' && vt[0] !== 0 ? vt[0] : 1;
    const scaleY = typeof vt[3] === 'number' && vt[3] !== 0 ? vt[3] : 1;
    return { x: dx / scaleX, y: dy / scaleY };
  }
  return { x: dx, y: dy };
}

function normalizeAngleTo180(angle) {
  let a = typeof angle === 'number' ? angle : 0;
  // 归一化到 [-180, 180)
  a = ((a % 360) + 360) % 360;
  if (a >= 180) {
    a -= 360;
  }
  return a;
}

function snapAngle(angle, interval, correctionRange) {
  const a = typeof angle === 'number' ? angle : 0;
  const step = typeof interval === 'number' && interval > 0 ? interval : 90;
  const range =
    typeof correctionRange === 'number' && correctionRange >= 0
      ? correctionRange
      : 0;
  const nearest = Math.round(a / step) * step;
  if (Math.abs(nearest - a) <= range) {
    return nearest;
  }
  return a;
}

function requestCanvasRender(fabricCanvas) {
  if (!fabricCanvas) {
    return;
  }
  try {
    if (typeof fabricCanvas.requestRenderAll === 'function') {
      fabricCanvas.requestRenderAll();
    } else if (typeof fabricCanvas.renderAll === 'function') {
      fabricCanvas.renderAll();
    }
  } catch (e) {
    // ignore
  }
}

/**
 * 启用 active object 的双指缩放/旋转/平移手势
 * - 仅双指生效；单指仍由 Fabric 默认交互处理
 * - 缩放/旋转围绕对象中心；平移跟随双指中心点移动
 * @param {fabric.Canvas} fabricCanvas
 * @param {Object} [options]
 * @param {number} [options.snapInterval=90] - 角度吸附间隔
 * @param {number} [options.snapRange=constant.SNAP_ANGLE_CORRECTION_RANGE] - 吸附范围（度）
 * @param {number} [options.minScale=0.05] - 最小缩放
 * @param {number} [options.maxScale=20] - 最大缩放
 */
export function enableActiveObjectPinchGesture(fabricCanvas, options) {
  if (!fabricCanvas) {
    return;
  }
  if (!activeObjectPinchGestureMap) {
    return;
  }
  if (activeObjectPinchGestureMap.has(fabricCanvas)) {
    return;
  }

  const upperCanvasEl = fabricCanvas.upperCanvasEl;
  if (!upperCanvasEl || typeof upperCanvasEl.addEventListener !== 'function') {
    return;
  }

  const opt = options || {};
  const snapInterval =
    typeof opt.snapInterval === 'number' && opt.snapInterval > 0
      ? opt.snapInterval
      : 90;
  const snapRange =
    typeof opt.snapRange === 'number' && opt.snapRange >= 0
      ? opt.snapRange
      : constant && typeof constant.SNAP_ANGLE_CORRECTION_RANGE === 'number'
      ? constant.SNAP_ANGLE_CORRECTION_RANGE
      : 3;
  const minScale = typeof opt.minScale === 'number' ? opt.minScale : 0.05;
  const maxScale = typeof opt.maxScale === 'number' ? opt.maxScale : 20;
  // 手势阈值：用于防抖，避免轻微抖动引起变化
  const rotateThresholdDeg =
    typeof opt.rotateThresholdDeg === 'number' && opt.rotateThresholdDeg >= 0
      ? opt.rotateThresholdDeg
      : 2;
  const scaleThresholdRatio =
    typeof opt.scaleThresholdRatio === 'number' && opt.scaleThresholdRatio >= 0
      ? opt.scaleThresholdRatio
      : 0.03;
  const minScaleDistanceForCalc =
    typeof opt.minScaleDistanceForCalc === 'number' &&
    opt.minScaleDistanceForCalc > 0
      ? opt.minScaleDistanceForCalc
      : 200;
  const translateEnabled =
    opt && typeof opt.translateEnabled === 'boolean'
      ? opt.translateEnabled
      : false;
  const translateThresholdPx =
    typeof opt.translateThresholdPx === 'number' &&
    opt.translateThresholdPx >= 0
      ? opt.translateThresholdPx
      : 8;

  const state = {
    isPinching: false,
    obj: null,
    startDistance: 0,
    startAngle: 0,
    startCenterCanvas: null,
    startObjScaleX: 1,
    startObjScaleY: 1,
    startObjAngle: 0,
    startObjCenter: null,
    snapInterval: snapInterval,
    snapRange: snapRange,
    minScale: minScale,
    maxScale: maxScale,
    rotateThresholdDeg: rotateThresholdDeg,
    scaleThresholdRatio: scaleThresholdRatio,
    minScaleDistanceForCalc: minScaleDistanceForCalc,
    translateEnabled: translateEnabled,
    translateThresholdPx: translateThresholdPx
  };

  function preventAndStop(e) {
    try {
      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }
      if (e && typeof e.stopPropagation === 'function') {
        e.stopPropagation();
      }
    } catch (err) {
      // ignore
    }
  }

  function resetState() {
    state.isPinching = false;
    state.obj = null;
    state.startDistance = 0;
    state.startAngle = 0;
    state.startCenterCanvas = null;
    state.startObjCenter = null;
  }

  function onTouchStart(e) {
    if (!activeObjectPinchGestureEnabled) {
      return;
    }
    if (!e || !e.touches || e.touches.length !== 2) {
      resetState();
      return;
    }

    const obj =
      fabricCanvas && typeof fabricCanvas.getActiveObject === 'function'
        ? fabricCanvas.getActiveObject()
        : null;
    if (!obj) {
      resetState();
      return;
    }

    const t0 = e.touches[0];
    const t1 = e.touches[1];

    state.isPinching = true;
    state.obj = obj;
    state.startDistance = getTouchDistance(t0, t1);
    state.startAngle = getTouchAngle(t0, t1);

    const centerPage = getTouchCenter(t0, t1);
    state.startCenterCanvas = getCanvasPointFromPage(upperCanvasEl, centerPage);

    state.startObjScaleX =
      typeof obj.scaleX === 'number' && obj.scaleX > 0 ? obj.scaleX : 1;
    state.startObjScaleY =
      typeof obj.scaleY === 'number' && obj.scaleY > 0 ? obj.scaleY : 1;
    state.startObjAngle = typeof obj.angle === 'number' ? obj.angle : 0;

    const center =
      obj.getCenterPoint && typeof obj.getCenterPoint === 'function'
        ? obj.getCenterPoint()
        : null;
    state.startObjCenter = center ? { x: center.x, y: center.y } : null;

    preventAndStop(e);
  }

  function onTouchMove(e) {
    if (!activeObjectPinchGestureEnabled) {
      return;
    }
    if (!state.isPinching) {
      return;
    }
    if (!e || !e.touches || e.touches.length !== 2) {
      return;
    }

    const activeObj =
      fabricCanvas && typeof fabricCanvas.getActiveObject === 'function'
        ? fabricCanvas.getActiveObject()
        : null;
    if (!activeObj || activeObj !== state.obj) {
      resetState();
      return;
    }

    const t0 = e.touches[0];
    const t1 = e.touches[1];

    const currDistance = getTouchDistance(t0, t1);
    if (!state.startDistance || !currDistance) {
      return;
    }
    const currAngle = getTouchAngle(t0, t1);
    const deltaAngle = normalizeAngleTo180(currAngle - state.startAngle);
    const absAngleChange = Math.abs(deltaAngle);

    const scaleDenom =
      state.startDistance > state.minScaleDistanceForCalc
        ? state.startDistance
        : state.minScaleDistanceForCalc;
    const scaleChange = (currDistance - state.startDistance) / scaleDenom;
    const absScaleChange = Math.abs(scaleChange);

    const shouldRotate = absAngleChange >= state.rotateThresholdDeg;
    const shouldScale = absScaleChange >= state.scaleThresholdRatio;

    if (!shouldRotate && !shouldScale) {
      preventAndStop(e);
      return;
    }

    const centerPage = getTouchCenter(t0, t1);
    const currCenterCanvas = getCanvasPointFromPage(upperCanvasEl, centerPage);
    const startCenterCanvas = state.startCenterCanvas || { x: 0, y: 0 };
    const rawDx = currCenterCanvas.x - startCenterCanvas.x;
    const rawDy = currCenterCanvas.y - startCenterCanvas.y;
    const delta = transformDeltaByViewport(fabricCanvas, rawDx, rawDy);
    const movedDistance = Math.sqrt(delta.x * delta.x + delta.y * delta.y);

    const baseCenter =
      state.startObjCenter ||
      (activeObj.getCenterPoint &&
      typeof activeObj.getCenterPoint === 'function'
        ? activeObj.getCenterPoint()
        : null);
    let targetCenter = baseCenter ? { x: baseCenter.x, y: baseCenter.y } : null;
    if (
      state.translateEnabled &&
      (shouldRotate || shouldScale) &&
      targetCenter &&
      movedDistance >= state.translateThresholdPx
    ) {
      targetCenter = {
        x: targetCenter.x + delta.x,
        y: targetCenter.y + delta.y
      };
    }

    let nextScaleX = state.startObjScaleX;
    let nextScaleY = state.startObjScaleY;
    let nextAngle = state.startObjAngle;

    if (shouldScale) {
      nextScaleX = state.startObjScaleX * (1 + scaleChange);
      nextScaleY = state.startObjScaleY * (1 + scaleChange);
      nextScaleX = clampNumber(nextScaleX, state.minScale, state.maxScale);
      nextScaleY = clampNumber(nextScaleY, state.minScale, state.maxScale);
    }

    if (shouldRotate) {
      nextAngle = state.startObjAngle + deltaAngle;
    }

    try {
      if (typeof activeObj.set === 'function') {
        activeObj.set({
          scaleX: nextScaleX,
          scaleY: nextScaleY,
          angle: nextAngle
        });
      } else {
        activeObj.scaleX = nextScaleX;
        activeObj.scaleY = nextScaleY;
        activeObj.angle = nextAngle;
      }

      if (
        targetCenter &&
        activeObj.setPositionByOrigin &&
        typeof activeObj.setPositionByOrigin === 'function'
      ) {
        activeObj.setPositionByOrigin(targetCenter, 'center', 'center');
      }

      if (activeObj.setCoords && typeof activeObj.setCoords === 'function') {
        activeObj.setCoords();
      }
      requestCanvasRender(fabricCanvas);
    } catch (err) {
      // ignore
    }

    preventAndStop(e);
  }

  function onTouchEnd(e) {
    if (!state.isPinching) {
      return;
    }
    const obj = state.obj;
    const originalAngle = state.startObjAngle;
    resetState();

    if (!obj) {
      return;
    }

    try {
      const currentAngle = typeof obj.angle === 'number' ? obj.angle : 0;
      const hasRotated = Math.abs(currentAngle - originalAngle) > 1;

      if (!hasRotated) {
        preventAndStop(e);
        return;
      }

      const center =
        obj.getCenterPoint && typeof obj.getCenterPoint === 'function'
          ? obj.getCenterPoint()
          : null;
      const snapped = snapAngle(currentAngle, snapInterval, snapRange);
      if (snapped !== currentAngle) {
        if (typeof obj.set === 'function') {
          obj.set('angle', snapped);
        } else {
          obj.angle = snapped;
        }
        if (
          center &&
          obj.setPositionByOrigin &&
          typeof obj.setPositionByOrigin === 'function'
        ) {
          obj.setPositionByOrigin(center, 'center', 'center');
        }
      }
      if (obj.setCoords && typeof obj.setCoords === 'function') {
        obj.setCoords();
      }
      requestCanvasRender(fabricCanvas);
    } catch (err) {
      // ignore
    }

    // 双指手势结束后补发 object:modified 事件
    // pinch util 接管了 touchstart 后 fabric 内部 mouse 事件循环被绕过，不会自动 fire object:modified
    // 此处补发以与单指拖拽保持一致，触发 fabric-canvas-wrapper 的 snap 以及 editImageNew 的 fitRemoveTransparent 修正
    try {
      if (fabricCanvas && typeof fabricCanvas.fire === 'function') {
        fabricCanvas.fire('object:modified', { target: obj, e: e });
      }
      if (obj && typeof obj.fire === 'function') {
        obj.fire('modified', { e: e });
      }
    } catch (err) {
      // ignore
    }

    preventAndStop(e);
  }

  let listenerOpt = false;
  try {
    listenerOpt = { passive: false };
  } catch (e) {
    listenerOpt = false;
  }

  upperCanvasEl.addEventListener('touchstart', onTouchStart, listenerOpt);
  upperCanvasEl.addEventListener('touchmove', onTouchMove, listenerOpt);
  upperCanvasEl.addEventListener('touchend', onTouchEnd, listenerOpt);
  upperCanvasEl.addEventListener('touchcancel', onTouchEnd, listenerOpt);

  activeObjectPinchGestureMap.set(fabricCanvas, {
    el: upperCanvasEl,
    opt: listenerOpt,
    onTouchStart,
    onTouchMove,
    onTouchEnd
  });
}

/**
 * 卸载 active object 的双指手势监听
 * @param {fabric.Canvas} fabricCanvas
 */
export function disableActiveObjectPinchGesture(fabricCanvas) {
  if (!fabricCanvas || !activeObjectPinchGestureMap) {
    return;
  }
  const record = activeObjectPinchGestureMap.get(fabricCanvas);
  if (!record) {
    return;
  }
  const el = record.el;
  const opt = record.opt;
  try {
    if (el && typeof el.removeEventListener === 'function') {
      el.removeEventListener('touchstart', record.onTouchStart, opt);
      el.removeEventListener('touchmove', record.onTouchMove, opt);
      el.removeEventListener('touchend', record.onTouchEnd, opt);
      el.removeEventListener('touchcancel', record.onTouchEnd, opt);
    }
  } catch (e) {
    // ignore
  }
  activeObjectPinchGestureMap.delete(fabricCanvas);
}
